import { useCallback } from 'react';
import Field from './Field';
import SchoolPicker from './SchoolPicker';
import SearchableSelect from './SearchableSelect';
import StateCityFields, { EMPTY_ADDRESS, addressPayload, isOtherCity } from './StateCityFields';
import * as v from '../validation/rules';
import { api } from '../api/client';

/**
 * Everything a teacher account is made of, as fields.
 *
 * <p>Two screens ask for it: a teacher registering themselves, and Support or an admin
 * creating one for a school that handed over a list. The document is the same document,
 * so the form is the same form — kept here rather than typed twice, because a field
 * added to one copy and not the other is a teacher registered with less than the person
 * beside them.
 *
 * <p>The parent owns the state and decides what surrounds it: a public page under a
 * heading, or a modal with a Create button. What differs between the two is passed in —
 * whether the password may be left blank for the server to generate, and whether the ID
 * proof is required — and nothing else.
 */

export const SALUTATIONS = [
  { id: 'MR', name: 'Mr.' },
  { id: 'MRS', name: 'Mrs.' },
  { id: 'MS', name: 'Ms.' },
  { id: 'DR', name: 'Dr.' },
];

/** The typed fields. Everything picked from a master lives in `picks` instead. */
export const EMPTY_TEACHER_VALUES = {
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  password: '',
  confirmPassword: '',
  affiliationCode: '',
  area: '',
  postalCode: '',
  subjectOther: '',
  idProof: null,
};

/**
 * Every picker's value is the whole `{ id, name }`, so a field can render what was
 * chosen without a second lookup to resolve the id back to a label. Salutation opens on
 * the common answer rather than on nothing, because the form marks it a dropdown.
 */
export const EMPTY_TEACHER_PICKS = {
  salutation: SALUTATIONS[0],
  school: { id: null, name: '' },
  address: EMPTY_ADDRESS,
  designation: null,
  classLevels: [],
  subjects: [],
  titles: [],
};

/**
 * @param requirePassword false where the server generates one if the field is left
 *   blank — an administrator creating an account for somebody else should not be
 *   inventing their password.
 * @param requireIdProof false where the person filling the form does not have the
 *   document: an admin creating the account has no ID to upload, and the teacher
 *   supplies it after signing in.
 */
export function teacherValidators({ requirePassword = true, requireIdProof = true } = {}) {
  return {
    firstName: v.firstName,
    lastName: v.lastName,
    mobile: v.mobile,
    email: v.email,
    password: requirePassword ? v.password : v.optional(v.password),
    // Left blank together: the re-entry has nothing to confirm, and demanding it would
    // make "leave it blank to generate one" impossible to actually do. The cross-field
    // rule still fires the moment either box has something in it.
    confirmPassword: requirePassword ? v.confirmPassword : v.optional(v.confirmPassword),
    affiliationCode: v.affiliationCode,
    area: v.area,
    postalCode: v.postalCode,
    idProof: v.idProof({ requiredFile: requireIdProof }),
  };
}

/** The JSON half of the request. The ID proof travels beside it, as its own part. */
export function teacherPayload(values, picks) {
  return {
    salutation: picks.salutation?.id ?? null,
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim() || null,
    mobile: v.normalizeMobile(values.mobile),
    email: v.normalizeEmail(values.email),
    password: values.password || null,
    schoolId: picks.school.id,
    schoolName: picks.school.id ? null : picks.school.name.trim() || null,
    affiliationCode: values.affiliationCode.trim() || null,
    area: values.area.trim() || null,
    ...addressPayload(picks.address),
    postalCode: values.postalCode.trim() || null,
    titleIds: picks.titles.map((title) => title.id),
    designationId: picks.designation?.id ?? null,
    classLevelIds: picks.classLevels.map((level) => level.id),
    subjectIds: picks.subjects.map((subject) => subject.id),
    subjectOther: values.subjectOther.trim() || null,
  };
}

/**
 * The one thing the pickers cannot catch themselves: "Other" chosen and nothing typed.
 * The choice is made in one field and answered in another, so neither can see it.
 *
 * @return the message to show against the city, or null when the address is complete
 */
export function cityGap(picks) {
  return isOtherCity(picks.address) && !picks.address.cityName.trim()
    ? 'Type the name of the city'
    : null;
}

export default function TeacherFields({
  form,
  picks,
  onPicks,
  cityError,
  requirePassword = true,
  requireIdProof = true,
  passwordHint,
}) {
  const set = (key) => (value) => onPicks({ ...picks, [key]: value });

  // Memoised, or a new function identity on every render would restart each picker's
  // debounced search on every keystroke elsewhere in the form.
  const searchSubjects = useCallback((q) => api.lookupSubjects(q), []);
  const loadDesignations = useCallback(() => api.lookupDesignations(), []);
  const loadClassLevels = useCallback(() => api.lookupClassLevels(), []);
  const loadTitles = useCallback(
    () => api.lookupTitles().then((rows) => rows.map((t) => ({ id: t.id, name: t.name, hint: t.code }))),
    [],
  );

  return (
    <>
      <div className="grid-3 grid-3-lead-narrow">
        <Field label="Salutation" id="salutation">
          <SearchableSelect
            id="salutation"
            value={picks.salutation}
            onChange={set('salutation')}
            options={SALUTATIONS}
            allowClear={false}
            placeholder="Mr."
          />
        </Field>
        <Field label="First name" id="firstName" required error={form.errorFor('firstName')}>
          <input className="input" autoComplete="given-name" maxLength={v.LIMITS.firstName.max}
                 {...form.field('firstName')} />
        </Field>
        <Field label="Last name" id="lastName" error={form.errorFor('lastName')}>
          <input className="input" autoComplete="family-name" maxLength={v.LIMITS.lastName.max}
                 {...form.field('lastName')} />
        </Field>
      </div>

      <SchoolPicker value={picks.school} onChange={set('school')} />

      <div className="grid-2">
        <Field
          label="Affiliation code"
          id="affiliationCode"
          hint="Board affiliation number, if there is one"
          error={form.errorFor('affiliationCode')}
        >
          <input className="input" maxLength={v.LIMITS.affiliationCode.max}
                 {...form.field('affiliationCode')} />
        </Field>
        <Field label="Area" id="area" error={form.errorFor('area')}>
          <input className="input" autoComplete="address-level3" maxLength={v.LIMITS.area.max}
                 {...form.field('area')} />
        </Field>
      </div>

      <StateCityFields value={picks.address} onChange={set('address')} cityError={cityError} />

      <Field label="Postal code" id="postalCode" error={form.errorFor('postalCode')}>
        <input className="input" autoComplete="postal-code" {...v.postalField(form)} />
      </Field>

      <div className="grid-2">
        <Field
          label="Fountainhead titles prescribed"
          id="titles"
          hint="The books the school has prescribed"
          error={form.errorFor('titles')}
        >
          <SearchableSelect
            id="titles"
            multiple
            value={picks.titles}
            onChange={set('titles')}
            loadOptions={loadTitles}
            placeholder="Search titles"
            emptyLabel="No title matches"
          />
        </Field>
        <Field label="Designation" id="designation" error={form.errorFor('designation')}>
          <SearchableSelect
            id="designation"
            value={picks.designation}
            onChange={set('designation')}
            loadOptions={loadDesignations}
            placeholder="Search designations"
          />
        </Field>
      </div>

      <div className="grid-2">
        <Field label="Classes handling" id="classLevels" hint="Every band they teach"
               error={form.errorFor('classLevels')}>
          <SearchableSelect
            id="classLevels"
            multiple
            value={picks.classLevels}
            onChange={set('classLevels')}
            loadOptions={loadClassLevels}
            placeholder="Primary, Middle, Secondary"
          />
        </Field>
        <Field label="Subjects" id="subjects" hint="Search and pick as many as apply"
               error={form.errorFor('subjects')}>
          <SearchableSelect
            id="subjects"
            multiple
            value={picks.subjects}
            onChange={set('subjects')}
            loadOptions={searchSubjects}
            maxItems={v.LIMITS.subjects.max}
            placeholder="Search subjects"
            emptyLabel="No subject matches"
          />
        </Field>
      </div>

      <Field
        label="Other subject"
        id="subjectOther"
        hint="Only if the subject taught is not in the list above"
        error={form.errorFor('subjectOther')}
      >
        <input className="input" maxLength={80} {...form.field('subjectOther')} />
      </Field>

      <div className="grid-2">
        <Field label="Email address" id="email" required error={form.errorFor('email')}>
          <input className="input" type="email" autoComplete="email" maxLength={255}
                 {...form.field('email')} />
        </Field>
        <Field label="Mobile number" id="mobile" required error={form.errorFor('mobile')}>
          <input className="input" {...v.mobileField(form)} />
        </Field>
      </div>

      <div className="grid-2">
        <Field label="Password" id="password" required={requirePassword}
               hint={passwordHint ?? v.PASSWORD_HINT} error={form.errorFor('password')}>
          <input className="input" type="password" autoComplete="new-password" maxLength={100}
                 {...form.field('password')} />
        </Field>
        <Field label="Re-enter password" id="confirmPassword" required={requirePassword}
               error={form.errorFor('confirmPassword')}>
          <input className="input" type="password" autoComplete="new-password" maxLength={100}
                 {...form.field('confirmPassword')} />
        </Field>
      </div>

      <Field
        label="Upload School ID"
        id="idProof"
        required={requireIdProof}
        hint={`${v.LIMITS.idProof.label}${requireIdProof
          ? ' — reviewed by Support during verification'
          : ' — optional here; the teacher can upload it after signing in'}`}
        error={form.errorFor('idProof')}
      >
        <input
          id="idProof"
          name="idProof"
          className="input"
          type="file"
          accept={v.LIMITS.idProof.types.join(',')}
          aria-invalid={form.showError('idProof') ? 'true' : undefined}
          aria-describedby={form.showError('idProof') ? 'idProof-error' : undefined}
          onBlur={() => form.markTouched('idProof')}
          onChange={(event) => form.setFieldValue('idProof', event.target.files?.[0] ?? null)}
        />
      </Field>
    </>
  );
}
